import { ReactNode } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Dropdown } from '@ui/components/Dropdown';
import { FormError, FormInput, FormLabel, FormRow } from '@ui/components/Form/Form';
import { Input } from '@ui/components/Input';
import { Tabs } from '@ui/components/Tabs';
import { TagListEditor } from '@ui/components/TagListEditor';
import { CameraIcon } from '@ui/icons/CameraIcon';

import { MINUTES_BEFORE_START_OPTIONS } from '@features/tournament/ui/components/TournamentSettings/constants';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings/typings';
import { VIDEO_SERVERS } from '@features/video/constants';

import styles from './TournamentEditFormInputs.module.css';

const ENABLE_TABS = [
  { value: false, label: 'Выключен' },
  { value: true, label: 'Включен' },
];

// Старт трансляции: те же варианты, что у закрытия регистрации, но от 1 минуты до 1 часа
const OPEN_BEFORE_START_OPTIONS = [
  { value: 1, label: 'За 1 минуту до начала' },
  ...MINUTES_BEFORE_START_OPTIONS.filter((o) => o.value <= 60),
];

export function VideoSettingsInput() {
  const { control } = useFormContext<TournamentEditFormValues>();
  const enabled = useWatch({ name: 'videoSettings.enable', control });
  const registrationCutoff = useWatch({ name: 'registrationClosedBeforeStartMinutes', control });
  // Предел для списка — по наблюдаемому значению. Правило валидации берет предел из values в
  // момент проверки: правила регистрируются при монтировании, и замыкание на наблюдаемое
  // значение устарело бы после смены закрытия регистрации
  const maxOpenBeforeStart = openLimit(registrationCutoff);

  return (
    <>
      <FormRow>
        <VideoLabel>Видеоконтроль</VideoLabel>
        <FormInput>
          <Controller
            name="videoSettings.enable"
            control={control}
            render={({ field: { value, onChange, disabled } }) => (
              <Tabs tabs={ENABLE_TABS} value={value} onChange={onChange} disabled={disabled} />
            )}
          />
        </FormInput>
      </FormRow>

      {enabled && (
        <>
          <FormRow>
            <VideoLabel>Серверы</VideoLabel>
            <FormInput>
              <Controller
                name="videoSettings.servers"
                control={control}
                rules={{ validate: validateServers }}
                render={({ field: { value, onChange, disabled }, fieldState }) => (
                  <div className={styles.stack}>
                    <TagListEditor
                      className={styles.servers}
                      value={value}
                      suggestions={VIDEO_SERVERS}
                      placeholder="https://video1.chessopen.ru"
                      onAdd={(server) => onChange([...value, server])}
                      onAddMany={(servers) => onChange([...value, ...servers])}
                      onRemove={(_server, index) => onChange(value.filter((_, i) => i !== index))}
                      disabled={disabled}
                    />
                    {fieldState.error && <FormError>{fieldState.error.message}</FormError>}
                  </div>
                )}
              />
            </FormInput>
          </FormRow>

          <FormRow>
            <VideoLabel>Размер комнаты</VideoLabel>
            <FormInput>
              <Controller
                name="videoSettings.roomCapacity"
                control={control}
                rules={{ validate: (v) => v >= 1 || 'Минимум 1 участник' }}
                render={({ field: { value, onChange, disabled }, fieldState }) => (
                  <div className={styles.stack}>
                    <Input
                      type="number"
                      invisible
                      min="1"
                      value={String(value)}
                      onChange={(v) => onChange(Number(v))}
                      disabled={disabled}
                      error={!!fieldState.error}
                      rightLabel="чел."
                    />
                    {fieldState.error && <FormError>{fieldState.error.message}</FormError>}
                  </div>
                )}
              />
            </FormInput>
          </FormRow>

          <FormRow>
            <VideoLabel hint="Не раньше закрытия регистрации">Старт трансляции</VideoLabel>
            <FormInput>
              <Controller
                name="videoSettings.openBeforeStartMinutes"
                control={control}
                rules={{
                  validate: (v, values) => {
                    const cutoff = values.registrationClosedBeforeStartMinutes;
                    return v <= openLimit(cutoff) || openLimitError(cutoff);
                  },
                }}
                render={({ field: { value, onChange, disabled }, fieldState }) => (
                  <div className={styles.stack}>
                    <Dropdown
                      options={OPEN_BEFORE_START_OPTIONS.map((o) => ({
                        ...o,
                        disabled: o.value > maxOpenBeforeStart,
                      }))}
                      value={String(value)}
                      onChange={(v) => onChange(Number(v))}
                      disabled={disabled}
                    />
                    {fieldState.error && <FormError>{fieldState.error.message}</FormError>}
                  </div>
                )}
              />
            </FormInput>
          </FormRow>
        </>
      )}
    </>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Лейбл с иконкой камеры: видеонастройки визуально сгруппированы
function VideoLabel({ hint, children }: { hint?: ReactNode; children: ReactNode }) {
  return (
    <FormLabel hint={hint}>
      <span className={styles.video_label}>
        <CameraIcon className={styles.video_label__icon} />
        {children}
      </span>
    </FormLabel>
  );
}

function validateServers(servers: string[]) {
  if (servers.length === 0) return 'Укажите хотя бы один сервер';
  return servers.every(isServerUrl) || 'Некорректный адрес сервера';
}

function isServerUrl(server: string) {
  try {
    return ['http:', 'https:'].includes(new URL(server).protocol);
  } catch {
    return false;
  }
}

// Предел старта трансляции: закрытие регистрации, а если она не закрывается — 1 минута
function openLimit(registrationCutoff: number | null): number {
  return registrationCutoff ?? 1;
}

function openLimitError(registrationCutoff: number | null): string {
  return registrationCutoff === null
    ? 'Регистрация не закрывается: время до начала трансляции не может превышать 1 минуты'
    : `Не раньше закрытия регистрации (${registrationCutoff} мин)`;
}
